import React from 'react';

import RelayDataID from '@atlassian/relay-data-id';
import { createRelayEnvironment } from '@atlassian/relay-environment';
import { SSRQueryResponseCache } from '@atlassian/relay-query-responses';
import { RelayEnvironmentProvider } from 'react-relay';
import { Environment, type GraphQLResponse } from 'relay-runtime';

import { getReleaseVersion, getPlatformEnvironment } from '@townsquare/config';
import localStorageFacade from '@townsquare/facade/local-storage';
import { fg } from '@townsquare/stat-sig/gate';

import { logMissingRequiredField } from './RelayEnvironment';
import { getAtlAttributionHeader } from './getAtlAttributionHeader';
import { getAggLoggingAndMetricsHelper } from './relayCaptureError';

export const AGG_BRANCH_OVERRIDE_KEY = 'townsquare.agg-branch-override';
export const AGG_LOCAL_TUNNEL_OVERRIDE_KEY = 'townsquare.agg-local-tunnel-override';

declare global {
	interface Window {
		__TOWNSQUARE__?: {
			clearAggOverride: () => void;
		};
	}
}

// Dev-only build-time overrides. '' = unset. Coerced for IT/VR bundlers that don't wire DefinePlugin.
const ENV_AGG_BRANCH_SLUG: string =
	typeof __AGG_BRANCH_SLUG__ === 'string' ? __AGG_BRANCH_SLUG__ : '';
const ENV_AGG_SERVICE_OVERRIDES: string =
	typeof __AGG_SERVICE_OVERRIDES__ === 'string' ? __AGG_SERVICE_OVERRIDES__ : '';

export const getAggBranchEnvOverride = (): string | undefined => ENV_AGG_BRANCH_SLUG || undefined;

export const getAggServiceEnvOverride = (): string | undefined =>
	ENV_AGG_SERVICE_OVERRIDES || undefined;

export const isAggBranchEnvOverridden = (): boolean => ENV_AGG_BRANCH_SLUG !== '';

export const isAggLocalTunnelEnvOverridden = (): boolean => ENV_AGG_SERVICE_OVERRIDES !== '';

if (typeof window !== 'undefined') {
	window.__TOWNSQUARE__ = {
		...(window.__TOWNSQUARE__ || {}),
		clearAggOverride: () => {
			localStorageFacade.removeItem(AGG_BRANCH_OVERRIDE_KEY);
			localStorageFacade.removeItem(AGG_LOCAL_TUNNEL_OVERRIDE_KEY);
			// eslint-disable-next-line no-console
			console.log('AGG endpoint overrides cleared. Reload the page for it to take effect.');
			if (ENV_AGG_BRANCH_SLUG !== '' || ENV_AGG_SERVICE_OVERRIDES !== '') {
				// eslint-disable-next-line no-console
				console.log(
					'Note: AGG_BRANCH_SLUG and/or AGG_SERVICE_OVERRIDES env vars are set and will continue to override the AGG endpoint regardless of localStorage.',
				);
			}
		},
	};
}

const readOverride = (key: string): string | null => {
	try {
		if (getPlatformEnvironment() === 'prod') {
			return null;
		}

		const override = localStorageFacade.getItem(key);

		if (!override) {
			return null;
		}

		return override;
	} catch {
		return null;
	}
};

const writeOverride = (key: string, value: string | null): void => {
	try {
		if (value) {
			localStorageFacade.setItem(key, value);
		} else {
			localStorageFacade.removeItem(key);
		}
	} catch {
		// localStorageFacade unavailable
	}
};

export const getAggBranchOverride = (): string | null => readOverride(AGG_BRANCH_OVERRIDE_KEY);

export const setAggBranchOverride = (branch: string | null): void =>
	writeOverride(AGG_BRANCH_OVERRIDE_KEY, branch);

export const getAggLocalTunnelOverride = (): string | null =>
	readOverride(AGG_LOCAL_TUNNEL_OVERRIDE_KEY);

export const setAggLocalTunnelOverride = (url: string | null): void =>
	writeOverride(AGG_LOCAL_TUNNEL_OVERRIDE_KEY, url);

const AGG_BASE_PATH = '/gateway/api/graphql';

const applyBranchOverride = (endpoint: string, branch: string): string => {
	if (!endpoint.startsWith(AGG_BASE_PATH)) {
		return endpoint;
	}

	const branchSegment = `${AGG_BASE_PATH}/branch-townsquare-${branch}/graphql`;
	return branchSegment + endpoint.slice(AGG_BASE_PATH.length);
};

const applyLocalTunnelOverride = (endpoint: string, tunnelUrl: string): string => {
	const trimmedTunnelUrl = tunnelUrl.replace(/\/$/, '');
	const serviceOverrideRawValue = `townsquare=${trimmedTunnelUrl}/graphql`;

	const queryIndex = endpoint.indexOf('?');
	if (queryIndex === -1) {
		return `${endpoint}?serviceOverride=${encodeURIComponent(serviceOverrideRawValue)}`;
	}

	const path = endpoint.slice(0, queryIndex);
	const search = endpoint.slice(queryIndex + 1);
	// eslint-disable-next-line no-restricted-syntax
	const params = new URLSearchParams(search);
	params.set('serviceOverride', serviceOverrideRawValue);
	return `${path}?${params.toString()}`;
};

// Env vars are applied upstream by @atlassian/relay-endpoint and intentionally
// suppress the localStorage transform — see FeatureControlPanel for UX rationale.
const setEndpoint = (endpoint: string) => {
	let result = endpoint;

	if (ENV_AGG_BRANCH_SLUG === '') {
		const branchOverride = getAggBranchOverride();
		if (branchOverride) {
			result = applyBranchOverride(result, branchOverride);
		}
	}

	if (ENV_AGG_SERVICE_OVERRIDES === '') {
		const localTunnelOverride = getAggLocalTunnelOverride();
		if (localTunnelOverride) {
			result = applyLocalTunnelOverride(result, localTunnelOverride);
		}
	}

	return result;
};

// AGG SSR preload cache. Mirror of `RelayPreloadedResponseStream.ts` for the
// AGG env. Keyed by {queryID, variables}; the TSQ-direct one keys by
// operationKey.
const aggQueryResponseCache = new SSRQueryResponseCache({
	cacheName: 'agg',
	size: 100,
	ttl: 60_000,
});

type AggPreloadQueueEntry = {
	queryID: string;
	queryName: string;
	variables: Record<string, unknown>;
	payload: unknown;
};

declare global {
	// `var` (not `let`) so the declaration lands on globalThis, matching the
	// `globalThis.__TOWNSQUARE_AGG_PRELOAD_QUEUE__` server emits.
	// eslint-disable-next-line no-var
	var __TOWNSQUARE_AGG_PRELOAD_QUEUE__:
		| AggPreloadQueueEntry[]
		| { push(entry: AggPreloadQueueEntry): void }
		| undefined;
}

// Browser-only: `relay-utils` is also imported by the SSR runtime, which has no `window`.
if (typeof window !== 'undefined') {
	const ingest = (entry: AggPreloadQueueEntry): void => {
		aggQueryResponseCache.appendWithMetadata(
			entry.queryID,
			entry.variables,
			entry.payload as GraphQLResponse,
			{ queryName: entry.queryName, operationKind: 'query' },
		);
	};

	const existing = globalThis.__TOWNSQUARE_AGG_PRELOAD_QUEUE__;
	if (Array.isArray(existing)) {
		for (const entry of existing) {
			ingest(entry);
		}
	}
	globalThis.__TOWNSQUARE_AGG_PRELOAD_QUEUE__ = { push: ingest };
}

const platformRelayEnvironment = createRelayEnvironment({
	requiredFieldLogger: logMissingRequiredField,
	getDataID: RelayDataID,
	networkOptions: {
		isServer: false,
		getHeaders: ({ request, variables }) => ({
			'atl-client-name': 'townsquare-frontend',
			'atl-client-version': getReleaseVersion(),
			...getAtlAttributionHeader(request, variables),
		}),
		setEndpoint: setEndpoint,
		queryResponseCache: aggQueryResponseCache,
		getLoggingAndMetricsHelper: getAggLoggingAndMetricsHelper,
	},
});

let platformRelayEnvironmentWithUgsHeaders: Environment;

export function getAggRelayEnvironment(): Environment {
	const isUGSHeadersMigrationEnabled = fg('townsquare_ugs_headers_migration');

	if (platformRelayEnvironmentWithUgsHeaders && isUGSHeadersMigrationEnabled) {
		return platformRelayEnvironmentWithUgsHeaders;
	}

	return platformRelayEnvironment;
}

export function initialiseAggRelayEnvironment(cloudId?: string): void {
	if (!platformRelayEnvironmentWithUgsHeaders) {
		platformRelayEnvironmentWithUgsHeaders = createRelayEnvironment({
			requiredFieldLogger: logMissingRequiredField,
			getDataID: RelayDataID,
			networkOptions: {
				isServer: false,
				getHeaders: ({ request, variables }) => ({
					'atl-client-name': 'townsquare-frontend',
					'atl-client-version': getReleaseVersion(),
					...(cloudId ? { 'X-Query-Context': `ari:cloud:platform::site/${cloudId}` } : {}),
					...getAtlAttributionHeader(request, variables),
				}),
				setEndpoint: setEndpoint,
				queryResponseCache: aggQueryResponseCache,
				getLoggingAndMetricsHelper: getAggLoggingAndMetricsHelper,
			},
		});
	}
}

interface AggRelayEnvironmentProviderProps {
	children: React.ReactNode | null;
}
export const AggRelayEnvironmentProvider = ({
	children,
}: AggRelayEnvironmentProviderProps): JSX.Element => {
	return (
		<RelayEnvironmentProvider environment={getAggRelayEnvironment()}>
			{children}
		</RelayEnvironmentProvider>
	);
};
